import React from "react";
import Layout from "../components/Layout";
import { useLang } from "../contexts/LanguageContext";
import { useLocation } from "react-router-dom";

export default function Legal() {
  const { lang } = useLang();
  const location = useLocation();
  // /mentions-legales → "mentions" ; /rgpd → "rgpd"
  const activeId = location.pathname.includes("rgpd") ? "rgpd" : "mentions";
  const c = lang === "fr" ? CONTENT_FR : CONTENT_EN;

  return (
    <Layout>
      <section className="pt-40 pb-12 md:pt-48 md:pb-16 px-6 md:px-10 max-w-[1100px] mx-auto">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand mb-6">— {c.eyebrow}</div>
        <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] font-light text-navy">
          {activeId === "rgpd" ? c.rgpd_title : c.mentions_title}
        </h1>
        <div className="mt-8 flex items-center gap-2 flex-wrap text-[11px] font-mono uppercase tracking-[0.2em]">
          <a href="/mentions-legales" data-testid="tab-mentions" className={`px-4 py-2 transition-colors ${activeId === "mentions" ? "bg-brand text-white" : "border border-black/10 text-ink/70 hover:border-brand hover:text-brand"}`}>{c.tab_mentions}</a>
          <a href="/rgpd" data-testid="tab-rgpd" className={`px-4 py-2 transition-colors ${activeId === "rgpd" ? "bg-brand text-white" : "border border-black/10 text-ink/70 hover:border-brand hover:text-brand"}`}>{c.tab_rgpd}</a>
        </div>
      </section>

      <section className="px-6 md:px-10 max-w-[1100px] mx-auto pb-28">
        <div className="prose-legal" data-testid={`legal-${activeId}`}>
          {(activeId === "rgpd" ? c.rgpd_sections : c.mentions_sections).map((s, i) => (
            <Block key={i} title={s.t} body={s.b} />
          ))}
        </div>
      </section>
    </Layout>
  );
}

function Block({ title, body }) {
  return (
    <div className="mb-12">
      <h2 className="font-serif text-2xl md:text-3xl font-light text-navy mb-4">{title}</h2>
      {body.map((p, i) =>
        Array.isArray(p) ? (
          <ul key={i} className="list-disc pl-6 text-ink/80 mb-4 space-y-1.5 leading-relaxed">
            {p.map((li, j) => (
              <li key={j} dangerouslySetInnerHTML={{ __html: li }} />
            ))}
          </ul>
        ) : (
          <p key={i} className="text-ink/80 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: p }} />
        )
      )}
    </div>
  );
}

const CONTENT_FR = {
  eyebrow: "Informations légales",
  mentions_title: "Mentions légales",
  rgpd_title: "Politique de confidentialité",
  tab_mentions: "Mentions légales",
  tab_rgpd: "RGPD · Confidentialité",
  mentions_sections: [
    {
      t: "Éditeur du site",
      b: [
        "<strong>Audace &amp; Perspectives</strong> — cabinet d'executive coaching et d'outplacement.",
        [
          "Siège social : 78 avenue des Champs-Élysées, 75008 Paris.",
          "Email : <a href=\"mailto:contact@audace-perspectives.com\" class=\"text-brand link-underline\">contact@audace-perspectives.com</a>",
          "Téléphone : +33 (0)6 52 30 30 00",
          "Directrice de la publication : Auriane de Potesta",
        ],
      ],
    },
    {
      t: "Hébergement",
      b: [
        "Le site <em>audace-perspectives.com</em> est hébergé sur une infrastructure cloud sécurisée (régions France &amp; Union européenne). Les bases de données et systèmes d'authentification sont opérés dans le respect du RGPD.",
      ],
    },
    {
      t: "Propriété intellectuelle",
      b: [
        "L'ensemble des contenus présents sur le site (textes, identité visuelle, photographies, illustrations, plaquettes PDF, logos, programmes d'accompagnement) est la propriété exclusive d'Audace &amp; Perspectives ou de ses partenaires. Toute reproduction, représentation ou diffusion, partielle ou totale, sans accord écrit préalable, est interdite.",
      ],
    },
    {
      t: "Confidentialité des accompagnements",
      b: [
        "Conformément au code éthique de la SF Coach, l'ensemble des informations échangées dans le cadre des séances de coaching et d'outplacement reste strictement confidentiel. L'espace coaché est privé et chiffré : seul le coaché et son coach ont accès au contenu des tests, séances, objectifs et documents.",
      ],
    },
    {
      t: "Crédits",
      b: [
        "Conception &amp; développement : équipe interne. Photographies : Unsplash (licences ouvertes) sauf mention contraire.",
      ],
    },
  ],
  rgpd_sections: [
    {
      t: "Engagement de confidentialité",
      b: [
        "Audace &amp; Perspectives respecte votre vie privée et traite les données personnelles dans le strict respect du Règlement (UE) 2016/679 du 27 avril 2016 (« RGPD ») et de la loi Informatique et Libertés modifiée.",
        "Cette politique décrit quelles données nous collectons, pourquoi, comment elles sont protégées et comment vous pouvez exercer vos droits.",
      ],
    },
    {
      t: "Responsable du traitement",
      b: [
        "Audace &amp; Perspectives — 78 avenue des Champs-Élysées, 75008 Paris. Contact : <a href=\"mailto:contact@audace-perspectives.com\" class=\"text-brand link-underline\">contact@audace-perspectives.com</a>",
      ],
    },
    {
      t: "Données collectées",
      b: [
        "Selon votre interaction avec le cabinet, nous pouvons être amenés à collecter :",
        [
          "<strong>Visiteurs du site</strong> : aucune donnée nominative — uniquement des journaux techniques anonymisés (sécurité, performance).",
          "<strong>Formulaire de contact / newsletter</strong> : nom, email, téléphone (facultatif), société (facultatif), sujet, message.",
          "<strong>Coachés accompagnés</strong> : nom, prénom, civilité, email, fonction, entreprise, programme suivi, résultats des tests psychométriques, comptes-rendus de séances et documents partagés via votre espace confidentiel.",
          "<strong>Évaluateurs 360°</strong> : nom, prénom, civilité, email, catégorie professionnelle, réponses au questionnaire 360°.",
        ],
      ],
    },
    {
      t: "Finalités et base légale",
      b: [
        [
          "<strong>Exécution d'un contrat</strong> : gestion de votre accompagnement (coaching, outplacement, formation, PSE), accès à l'espace confidentiel, génération des bilans PDF.",
          "<strong>Consentement</strong> : envoi de la newsletter, prise de contact via le formulaire du site.",
          "<strong>Intérêt légitime</strong> : sécurité du site, prévention des abus, statistiques anonymisées.",
          "<strong>Obligation légale</strong> : conservation des pièces comptables pendant la durée légale applicable.",
        ],
      ],
    },
    {
      t: "Durées de conservation",
      b: [
        [
          "Demandes de contact : 3 ans à compter du dernier échange.",
          "Données d'accompagnement (coachés, tests, séances) : durée de l'accompagnement + 5 ans pour répondre à d'éventuelles obligations contractuelles ou réglementaires.",
          "Évaluations 360° : conservées le temps de l'accompagnement puis archivées de façon anonymisée pendant 3 ans.",
          "Newsletter : tant que vous ne vous désinscrivez pas (lien présent dans chaque envoi).",
        ],
      ],
    },
    {
      t: "Destinataires",
      b: [
        "Vos données restent confidentielles et ne sont accessibles qu'à l'équipe d'Audace &amp; Perspectives strictement habilitée. Les évaluations 360° sont synthétisées de manière agrégée et anonyme : les évaluateurs ne sont jamais identifiés nominativement dans le rapport remis au coaché.",
        "Sous-traitants techniques (hébergement, messagerie transactionnelle) : <em>Resend</em> pour l'envoi des emails, infrastructures cloud européennes pour l'hébergement applicatif et base de données. Tous sont liés par un contrat conforme à l'article 28 du RGPD.",
      ],
    },
    {
      t: "Sécurité",
      b: [
        "Mots de passe hachés (bcrypt), authentification par cookies <em>HttpOnly Secure SameSite</em>, transports chiffrés (HTTPS/TLS), accès à l'espace confidentiel strictement nominatif. Les espaces coaché et administrateur sont strictement séparés.",
      ],
    },
    {
      t: "Vos droits",
      b: [
        "Conformément aux articles 15 à 22 du RGPD, vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation, d'opposition et de portabilité sur vos données. Vous pouvez également définir des directives relatives au sort de vos données après votre décès.",
        "Pour exercer ces droits, écrivez à <a href=\"mailto:contact@audace-perspectives.com\" class=\"text-brand link-underline\">contact@audace-perspectives.com</a>. Une réponse vous sera apportée dans un délai d'un mois.",
        "Vous pouvez également introduire une réclamation auprès de la CNIL (<a href=\"https://www.cnil.fr\" target=\"_blank\" rel=\"noopener noreferrer\" class=\"text-brand link-underline\">cnil.fr</a>).",
      ],
    },
    {
      t: "Cookies",
      b: [
        "Le site n'utilise que des cookies strictement nécessaires à l'authentification dans l'espace confidentiel (`access_token`, `refresh_token`). Aucun cookie publicitaire ou de mesure d'audience tiers n'est déposé.",
      ],
    },
  ],
};

const CONTENT_EN = {
  eyebrow: "Legal information",
  mentions_title: "Legal notice",
  rgpd_title: "Privacy policy",
  tab_mentions: "Legal notice",
  tab_rgpd: "GDPR · Privacy",
  mentions_sections: [
    {
      t: "Publisher",
      b: [
        "<strong>Audace &amp; Perspectives</strong> — executive coaching and outplacement consultancy.",
        [
          "Registered office: 78 avenue des Champs-Élysées, 75008 Paris, France.",
          "Email: <a href=\"mailto:contact@audace-perspectives.com\" class=\"text-brand link-underline\">contact@audace-perspectives.com</a>",
          "Phone: +33 (0)6 52 30 30 00",
          "Publication director: Auriane de Potesta",
        ],
      ],
    },
    {
      t: "Hosting",
      b: [
        "The website <em>audace-perspectives.com</em> is hosted on secure cloud infrastructure (France &amp; EU regions). Databases and authentication systems are operated in compliance with the GDPR.",
      ],
    },
    {
      t: "Intellectual property",
      b: [
        "All content on the site (texts, visual identity, photographs, illustrations, PDF brochures, logos, coaching programmes) is the exclusive property of Audace &amp; Perspectives or its partners. Any reproduction, representation or distribution, in whole or in part, without prior written consent, is prohibited.",
      ],
    },
    {
      t: "Confidentiality of engagements",
      b: [
        "In line with the SF Coach ethics code, all information shared during coaching and outplacement sessions remains strictly confidential. The coachée workspace is private and encrypted: only the coachée and their coach have access to test results, sessions, objectives and documents.",
      ],
    },
    {
      t: "Credits",
      b: [
        "Design &amp; development: internal team. Photography: Unsplash (open licences) unless otherwise stated.",
      ],
    },
  ],
  rgpd_sections: [
    {
      t: "Privacy commitment",
      b: [
        "Audace &amp; Perspectives respects your privacy and processes personal data in strict compliance with EU Regulation 2016/679 of 27 April 2016 (\"GDPR\") and the French Data Protection Act.",
        "This policy describes the data we collect, why, how it is protected and how you can exercise your rights.",
      ],
    },
    {
      t: "Data controller",
      b: [
        "Audace &amp; Perspectives — 78 avenue des Champs-Élysées, 75008 Paris, France. Contact: <a href=\"mailto:contact@audace-perspectives.com\" class=\"text-brand link-underline\">contact@audace-perspectives.com</a>",
      ],
    },
    {
      t: "Data collected",
      b: [
        "Depending on your interaction with the firm, we may collect:",
        [
          "<strong>Website visitors</strong>: no personal data — only anonymised technical logs (security, performance).",
          "<strong>Contact / newsletter form</strong>: name, email, phone (optional), company (optional), subject, message.",
          "<strong>Coachées</strong>: name, first name, title, email, function, company, programme, psychometric test results, session notes and documents shared in the coachée workspace.",
          "<strong>360° raters</strong>: name, first name, title, email, professional category, 360° questionnaire responses.",
        ],
      ],
    },
    {
      t: "Purposes and legal basis",
      b: [
        [
          "<strong>Contract performance</strong>: managing your engagement (coaching, outplacement, training, JPP), access to the coachée workspace, generation of PDF reports.",
          "<strong>Consent</strong>: newsletter sign-up, contact via the website form.",
          "<strong>Legitimate interest</strong>: site security, abuse prevention, anonymised statistics.",
          "<strong>Legal obligation</strong>: retention of accounting records for the legally required period.",
        ],
      ],
    },
    {
      t: "Retention periods",
      b: [
        [
          "Contact requests: 3 years from the last exchange.",
          "Engagement data (coachées, tests, sessions): duration of the engagement + 5 years to meet any contractual or regulatory obligations.",
          "360° evaluations: kept for the duration of the engagement, then archived anonymously for 3 years.",
          "Newsletter: until you unsubscribe (link present in every email).",
        ],
      ],
    },
    {
      t: "Recipients",
      b: [
        "Your data remains confidential and is only accessible to strictly authorised members of the Audace &amp; Perspectives team. 360° evaluations are aggregated and anonymised: raters are never individually identified in the coachée report.",
        "Technical sub-processors (hosting, transactional email): <em>Resend</em> for email delivery, European cloud infrastructure for application hosting and database. All are bound by a contract compliant with Article 28 GDPR.",
      ],
    },
    {
      t: "Security",
      b: [
        "Passwords hashed (bcrypt), authentication via <em>HttpOnly Secure SameSite</em> cookies, encrypted transport (HTTPS/TLS), strictly name-based access to the coachée workspace. Coachée and administrator workspaces are strictly separated.",
      ],
    },
    {
      t: "Your rights",
      b: [
        "Under Articles 15 to 22 GDPR, you have a right of access, rectification, erasure, restriction, objection and portability over your data. You may also define directives regarding the fate of your data after your death.",
        "To exercise these rights, write to <a href=\"mailto:contact@audace-perspectives.com\" class=\"text-brand link-underline\">contact@audace-perspectives.com</a>. We will reply within one month.",
        "You may also lodge a complaint with the CNIL (<a href=\"https://www.cnil.fr/en\" target=\"_blank\" rel=\"noopener noreferrer\" class=\"text-brand link-underline\">cnil.fr</a>).",
      ],
    },
    {
      t: "Cookies",
      b: [
        "The site only uses strictly necessary cookies for authentication in the coachée workspace (`access_token`, `refresh_token`). No advertising or third-party analytics cookies are placed.",
      ],
    },
  ],
};
